/**
 * VacationAccountsPage - Admin/HR-Verwaltungsseite für Urlaubskonten
 * 
 * Features:
 * - Übersichtstabelle aller Mitarbeiter mit Urlaubskontostand
 * - Fällige Hinweise anzeigen und versenden
 * - Manuelle Zusatztage mit Pflicht-Notiz
 * - Jahresübergang mit Vorschau und Bestätigung
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format, parseISO, differenceInWeeks } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Users,
  Palmtree,
  AlertTriangle,
  Bell,
  Send,
  SendHorizonal,
  Plus,
  Minus,
  RefreshCw,
  Calendar,
  ChevronRight,
  Check,
  X,
  Info,
  Clock,
  CalendarClock,
  ArrowRightCircle,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function VacationAccountsPage() {
  const { user, api } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [dueReminders, setDueReminders] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialoge
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustUser, setAdjustUser] = useState(null);
  const [adjustDays, setAdjustDays] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  
  const [carryOverDialogOpen, setCarryOverDialogOpen] = useState(false);
  const [carryOverYear, setCarryOverYear] = useState(new Date().getFullYear() - 1);
  const [carryOverPreview, setCarryOverPreview] = useState(null);
  const [isCarryingOver, setIsCarryingOver] = useState(false);
  const [carryOverStep, setCarryOverStep] = useState('select'); // select, preview, confirm
  
  const [sendReminderDialogOpen, setSendReminderDialogOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  // Daten laden
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Benutzer laden
      const usersRes = await api.get('/users');
      const activeUsers = usersRes.data.filter(u => u.is_active !== false);
      setUsers(activeUsers);
      
      // Konten aller Benutzer und fällige Hinweise gleichzeitig laden statt nacheinander (G24 Ladezeit)
      const [accountResults, dueRes] = await Promise.all([
        Promise.all(activeUsers.map(u =>
          api.get(`/vacation-accounts/${u.id}/${selectedYear}`)
            .then(res => res.data)
            // Konto existiert möglicherweise noch nicht
            .catch(() => null)
        )),
        api.get('/vacation-accounts/reminders/due').catch(err => {
          console.error('Failed to fetch due reminders:', err);
          return null;
        }),
      ]);
      const accountsMap = {};
      activeUsers.forEach((u, i) => { accountsMap[u.id] = accountResults[i]; });
      setAccounts(accountsMap);
      if (dueRes) setDueReminders(dueRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  }, [api, selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Formatierung
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'd. MMM yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  // Warnung berechnen
  const needsAttention = (acc) => {
    if (!acc) return false;
    const today = new Date();
    if (acc.expiry_next_date) {
      const expiry = parseISO(acc.expiry_next_date);
      const weeks = differenceInWeeks(expiry, today);
      return weeks < 8 && acc.expiry_next_days > 0;
    }
    return false;
  };

  // Hinweisstatus prüfen
  const hasAnnualNotice = (userId) => {
    if (!dueReminders) return true; // Lade noch
    const annual = dueReminders.annual_notices || [];
    return !annual.some(a => a.user_id === userId);
  };

  // Filter
  const filteredUsers = users.filter(u => {
    const name = `${u.first_name} ${u.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  // Sortieren nach Dringlichkeit
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const accA = accounts[a.id];
    const accB = accounts[b.id];
    const needsA = needsAttention(accA);
    const needsB = needsAttention(accB);
    const noticeA = hasAnnualNotice(a.id);
    const noticeB = hasAnnualNotice(b.id);
    
    // Erst Warnung, dann fehlender Hinweis
    if (needsA && !needsB) return -1;
    if (!needsA && needsB) return 1;
    if (!noticeA && noticeB) return -1;
    if (noticeA && !noticeB) return 1;
    
    return a.last_name.localeCompare(b.last_name);
  });

  // Manuelle Anpassung
  const openAdjustDialog = (targetUser) => {
    setAdjustUser(targetUser);
    setAdjustDays('');
    setAdjustNote('');
    setAdjustDialogOpen(true);
  };

  const handleAdjust = async () => {
    if (!adjustNote.trim()) {
      toast.error('Begründung ist erforderlich');
      return;
    }
    
    const days = parseFloat(adjustDays);
    if (isNaN(days) || days === 0) {
      toast.error('Bitte geben Sie eine gültige Anzahl ein');
      return;
    }
    
    setIsAdjusting(true);
    try {
      await api.post(`/vacation-accounts/${adjustUser.id}/${selectedYear}/adjust`, {
        delta_days: days,
        note: adjustNote.trim(),
      });
      toast.success('Anpassung erfolgreich');
      setAdjustDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Anpassung');
    } finally {
      setIsAdjusting(false);
    }
  };

  // Hinweis versenden
  const openSendReminderDialog = (target) => {
    setReminderTarget(target);
    setSendReminderDialogOpen(true);
  };

  const handleSendReminder = async () => {
    if (!reminderTarget) return;
    
    setIsSendingReminder(true);
    try {
      await api.post('/vacation-accounts/reminders/send', null, {
        params: {
          user_id: reminderTarget.user_id,
          year: reminderTarget.year,
          reminder_type: reminderTarget.reminder_type,
        }
      });
      toast.success(`Hinweis an ${reminderTarget.first_name} ${reminderTarget.last_name} gesendet`);
      setSendReminderDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Versenden');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleSendAllDue = async () => {
    if (!window.confirm('Alle fälligen Hinweise jetzt versenden?')) return;
    
    setIsSendingReminder(true);
    try {
      const res = await api.post('/vacation-accounts/reminders/send-all-due');
      toast.success(`${res.data.sent_count} Hinweise versendet`);
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} Fehler aufgetreten`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Versenden');
    } finally {
      setIsSendingReminder(false);
    }
  };

  // Jahresübergang
  const openCarryOverDialog = () => {
    setCarryOverYear(currentYear - 1);
    setCarryOverPreview(null);
    setCarryOverStep('select');
    setCarryOverDialogOpen(true);
  };

  const handleCarryOverPreview = async () => {
    setIsCarryingOver(true);
    try {
      const res = await api.post('/vacation-accounts/carry-over', {
        year: carryOverYear,
      }, {
        params: { dry_run: true }
      });
      setCarryOverPreview(res.data);
      setCarryOverStep('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Vorschau');
    } finally {
      setIsCarryingOver(false);
    }
  };

  const handleCarryOverExecute = async () => {
    if (!window.confirm(
      `Jahresübergang von ${carryOverYear} zu ${carryOverYear + 1} wirklich durchführen?\n\n` +
      `Dies kann nicht rückgängig gemacht werden!`
    )) return;
    
    setIsCarryingOver(true);
    try {
      const res = await api.post('/vacation-accounts/carry-over', {
        year: carryOverYear,
      }, {
        params: { dry_run: false }
      });
      toast.success(`Jahresübergang abgeschlossen: ${res.data.user_count} Konten verarbeitet`);
      setCarryOverDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Jahresübergang');
    } finally {
      setIsCarryingOver(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalDue = dueReminders?.total_due || 0;

  return (
    <div data-testid="vacation-accounts-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Urlaubskonten</h1>
          <p className="text-muted-foreground mt-1">Verwaltung der Urlaubsansprüche und Hinweispflicht</p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] bg-input border-border text-foreground" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            onClick={openCarryOverDialog}
            className="bg-indigo-500 hover:bg-indigo-600"
            data-testid="carry-over-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Jahresübergang
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-card/50 border border-border">
          <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-500/20">
            <Users className="w-4 h-4 mr-2" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="reminders" className="data-[state=active]:bg-amber-500/20">
            <Bell className="w-4 h-4 mr-2" />
            Fällige Hinweise ({totalDue})
          </TabsTrigger>
        </TabsList>

        {/* Übersicht Tab */}
        <TabsContent value="overview" className="mt-6">
          {/* Suchfeld */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Mitarbeiter suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input border-border text-foreground"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Tabelle */}
          <div className="card-glass overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b border-border">
                    <th className="p-4">Mitarbeiter</th>
                    <th className="p-4 text-right">Anspruch</th>
                    <th className="p-4 text-right">Übertrag</th>
                    <th className="p-4 text-right">Genommen</th>
                    <th className="p-4 text-right">Rest</th>
                    <th className="p-4">Verfall</th>
                    <th className="p-4">Hinweis</th>
                    <th className="p-4">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => {
                    const acc = accounts[u.id];
                    const attention = needsAttention(acc);
                    const hasNotice = hasAnnualNotice(u.id);
                    
                    return (
                      <tr 
                        key={u.id} 
                        className={`border-b border-border hover:bg-accent ${
                          attention ? 'bg-amber-500/5' : ''
                        }`}
                        data-testid={`user-row-${u.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {attention && (
                              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                            )}
                            <div>
                              <p className="text-foreground font-medium">
                                {u.first_name} {u.last_name}
                              </p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-right text-foreground font-mono">
                          {acc?.base_entitlement?.toFixed(1) ?? '-'}
                        </td>
                        <td className="p-4 text-right font-mono">
                          <span className={acc?.carryover_from_previous > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-muted-foreground'}>
                            {acc?.carryover_from_previous?.toFixed(1) ?? '-'}
                          </span>
                        </td>
                        <td className="p-4 text-right text-amber-600 dark:text-amber-400 font-mono">
                          {acc?.used_days?.toFixed(1) ?? '-'}
                        </td>
                        <td className="p-4 text-right text-emerald-600 dark:text-emerald-400 font-mono font-bold">
                          {acc?.remaining_total?.toFixed(1) ?? '-'}
                        </td>
                        <td className="p-4">
                          {acc?.expiry_next_date && acc?.expiry_next_days > 0 ? (
                            <div className={attention ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}>
                              <p className="text-sm">{acc.expiry_next_days?.toFixed(1)} Tage</p>
                              <p className="text-xs">{formatDate(acc.expiry_next_date)}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          {hasNotice ? (
                            <span className="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-400">
                              <Check className="w-3 h-3 inline mr-1" />
                              Erteilt
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              Ausstehend
                            </span>
                          )}
                        </td>
                        <td className="p-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAdjustDialog(u)}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid={`adjust-${u.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Fällige Hinweise Tab */}
        <TabsContent value="reminders" className="mt-6 space-y-6">
          {/* Gesamtversand Button */}
          {totalDue > 0 && (
            <div className="flex justify-end">
              <Button
                onClick={handleSendAllDue}
                disabled={isSendingReminder}
                className="bg-indigo-500 hover:bg-indigo-600"
                data-testid="send-all-reminders"
              >
                <SendHorizonal className="w-4 h-4 mr-2" />
                Alle {totalDue} Hinweise versenden
              </Button>
            </div>
          )}

          {/* Jährliche Hinweise */}
          {(dueReminders?.annual_notices?.length || 0) > 0 && (
            <div className="card-glass p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Jährliche Urlaubshinweise
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Gemäß § 7 Abs. 3 BUrlG müssen Arbeitgeber ihre Mitarbeiter rechtzeitig auf den offenen Resturlaub hinweisen.
              </p>
              <div className="space-y-3">
                {dueReminders.annual_notices.map((item) => (
                  <div 
                    key={`annual-${item.user_id}`}
                    className="flex items-center justify-between p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                  >
                    <div>
                      <p className="text-foreground font-medium">{item.first_name} {item.last_name}</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {item.remaining_total?.toFixed(1)} Tage offen für {item.year}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => openSendReminderDialog(item)}
                      className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30"
                      data-testid={`send-annual-${item.user_id}`}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Hinweis senden
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Verfallswarnungen (laufendes Jahr) */}
          {(dueReminders?.expiry_warnings?.length || 0) > 0 && (
            <div className="card-glass p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Verfallswarnungen (Jahresurlaub)
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Der Urlaub dieser Mitarbeiter verfällt zum Jahresende.
              </p>
              <div className="space-y-3">
                {dueReminders.expiry_warnings.map((item) => (
                  <div 
                    key={`expiry-${item.user_id}`}
                    className="flex items-center justify-between p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg"
                  >
                    <div>
                      <p className="text-foreground font-medium">{item.first_name} {item.last_name}</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {item.remaining_days?.toFixed(1)} Tage verfallen am {formatDate(item.expiry_date)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => openSendReminderDialog(item)}
                      className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30"
                      data-testid={`send-expiry-${item.user_id}`}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Warnung senden
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Übertrag-Verfallswarnungen */}
          {(dueReminders?.carryover_warnings?.length || 0) > 0 && (
            <div className="card-glass p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <ArrowRightCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                Übertrag-Verfallswarnungen
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Der Übertrag aus dem Vorjahr dieser Mitarbeiter verfällt zum 31. März.
              </p>
              <div className="space-y-3">
                {dueReminders.carryover_warnings.map((item) => (
                  <div 
                    key={`carryover-${item.user_id}`}
                    className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
                  >
                    <div>
                      <p className="text-foreground font-medium">{item.first_name} {item.last_name}</p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {item.remaining_days?.toFixed(1)} Übertragstage verfallen am {formatDate(item.expiry_date)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => openSendReminderDialog(item)}
                      className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                      data-testid={`send-carryover-${item.user_id}`}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Warnung senden
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keine fälligen Hinweise */}
          {totalDue === 0 && (
            <div className="card-glass p-8 text-center">
              <Check className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Keine fälligen Hinweise</h3>
              <p className="text-muted-foreground">
                Alle Mitarbeiter wurden über ihren Resturlaub informiert.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Anpassungs-Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Urlaubskonto anpassen</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {adjustUser && (
                <>Zusatztage für {adjustUser.first_name} {adjustUser.last_name} ({selectedYear})</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-muted-foreground">Tage (+/-)</Label>
              <Input
                type="number"
                step="0.5"
                value={adjustDays}
                onChange={(e) => setAdjustDays(e.target.value)}
                placeholder="z.B. 2 oder -1"
                className="bg-input border-border text-foreground mt-1"
                data-testid="adjust-days-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Positive Werte für Zusatzurlaub, negative für Abzüge
              </p>
            </div>
            
            <div>
              <Label className="text-muted-foreground">Begründung (Pflichtfeld)</Label>
              <Textarea
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
                placeholder="z.B. Sonderurlaub für Umzug"
                className="bg-input border-border text-foreground mt-1"
                rows={3}
                data-testid="adjust-note-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdjustDialogOpen(false)} className="text-muted-foreground">
              Abbrechen
            </Button>
            <Button 
              onClick={handleAdjust} 
              disabled={isAdjusting || !adjustNote.trim()}
              className="bg-indigo-500 hover:bg-indigo-600"
              data-testid="confirm-adjust"
            >
              {isAdjusting ? 'Speichere...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hinweis-Versand-Dialog */}
      <Dialog open={sendReminderDialogOpen} onOpenChange={setSendReminderDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Hinweis versenden</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {reminderTarget && (
                <>
                  {reminderTarget.reminder_type === 'annual_notice' && 'Jährlicher Urlaubshinweis'}
                  {reminderTarget.reminder_type === 'expiry_warning' && 'Verfallswarnung'}
                  {reminderTarget.reminder_type === 'carryover_warning' && 'Übertrag-Verfallswarnung'}
                  {' '}an {reminderTarget.first_name} {reminderTarget.last_name}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {reminderTarget && (
            <div className="py-4">
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <p className="text-foreground">
                  <strong>Mitarbeiter:</strong> {reminderTarget.first_name} {reminderTarget.last_name}
                </p>
                <p className="text-foreground">
                  <strong>Offene Tage:</strong> {(reminderTarget.remaining_total || reminderTarget.remaining_days)?.toFixed(1)}
                </p>
                <p className="text-foreground">
                  <strong>Verfallsdatum:</strong> {formatDate(reminderTarget.expiry_date)}
                </p>
              </div>
              
              <p className="text-sm text-muted-foreground mt-4">
                Der Hinweis wird in der App angezeigt und der Mitarbeiter muss ihn bestätigen.
                Der genaue Wortlaut wird protokolliert.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendReminderDialogOpen(false)} className="text-muted-foreground">
              Abbrechen
            </Button>
            <Button 
              onClick={handleSendReminder} 
              disabled={isSendingReminder}
              className="bg-indigo-500 hover:bg-indigo-600"
              data-testid="confirm-send-reminder"
            >
              {isSendingReminder ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sende...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Hinweis versenden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Jahresübergang-Dialog */}
      <Dialog open={carryOverDialogOpen} onOpenChange={setCarryOverDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground">Jahresübergang</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Übertrag von Resturlaub und Prüfung der Hinweispflicht
            </DialogDescription>
          </DialogHeader>
          
          {carryOverStep === 'select' && (
            <div className="py-4 space-y-4">
              <div>
                <Label className="text-muted-foreground">Von Jahr</Label>
                <Select value={carryOverYear.toString()} onValueChange={(v) => setCarryOverYear(parseInt(v))}>
                  <SelectTrigger className="bg-input border-border text-foreground mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {[currentYear - 2, currentYear - 1, currentYear].map((year) => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700 dark:text-amber-300">
                    <p className="font-medium">Wichtige Hinweise:</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      <li>Der Jahresübergang schließt das Konto für {carryOverYear} ab</li>
                      <li>Nicht genommener Urlaub wird zum {carryOverYear + 1} übertragen</li>
                      <li>Ohne dokumentierten Hinweis verfällt kein Urlaub</li>
                      <li>Diese Aktion kann nicht rückgängig gemacht werden</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {carryOverStep === 'preview' && carryOverPreview && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-foreground">{carryOverPreview.user_count}</p>
                  <p className="text-sm text-muted-foreground">Mitarbeiter</p>
                </div>
                <div className="p-4 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                    {carryOverPreview.total_carryover_days?.toFixed(1)}
                  </p>
                  <p className="text-sm text-muted-foreground">Tage Übertrag</p>
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {carryOverPreview.total_expired_days?.toFixed(1)}
                  </p>
                  <p className="text-sm text-muted-foreground">Tage Verfall</p>
                </div>
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left p-2">Mitarbeiter</th>
                      <th className="text-right p-2">Übertrag</th>
                      <th className="text-right p-2">Verfall</th>
                      <th className="text-center p-2">Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carryOverPreview.results?.map((r) => (
                      <tr key={r.user_id} className="border-b border-border">
                        <td className="p-2 text-foreground">{r.user_name}</td>
                        <td className="p-2 text-right text-emerald-600 dark:text-emerald-400">{r.carryover_days?.toFixed(1)}</td>
                        <td className="p-2 text-right text-red-600 dark:text-red-400">{r.expired_days?.toFixed(1)}</td>
                        <td className="p-2 text-center">
                          {r.notice_fulfilled ? (
                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 inline" />
                          ) : (
                            <X className="w-4 h-4 text-amber-600 dark:text-amber-400 inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {carryOverPreview.results?.some(r => !r.notice_fulfilled) && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Für einige Mitarbeiter wurde die Hinweispflicht nicht erfüllt. 
                      Deren Urlaub verfällt daher <strong>nicht</strong> und wird vollständig übertragen.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCarryOverDialogOpen(false)} className="text-muted-foreground">
              Abbrechen
            </Button>
            
            {carryOverStep === 'select' && (
              <Button 
                onClick={handleCarryOverPreview} 
                disabled={isCarryingOver}
                className="bg-indigo-500 hover:bg-indigo-600"
                data-testid="carry-over-preview"
              >
                {isCarryingOver ? 'Lade...' : 'Vorschau anzeigen'}
              </Button>
            )}
            
            {carryOverStep === 'preview' && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setCarryOverStep('select')}
                  className="border-border"
                >
                  Zurück
                </Button>
                <Button 
                  onClick={handleCarryOverExecute} 
                  disabled={isCarryingOver}
                  className="bg-emerald-500 hover:bg-emerald-600"
                  data-testid="carry-over-execute"
                >
                  {isCarryingOver ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Verarbeite...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Jahresübergang durchführen
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
